import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FaGithub, FaXTwitter } from 'react-icons/fa6';
import { HiOutlineMail, HiOutlineDocumentDownload } from 'react-icons/hi';
import { BlockCard } from './BlockCard';
import { ChainConnector } from './ChainConnector';
import { ChainModal } from './ChainModal';
import { CvPreviewModal } from './CvPreviewModal';
import { BLOCKS } from '../data/chain';
import { CHAIN_DECORATIONS, getChainById } from '../data/chains';
import type { ChainExperience } from '../data/chains';
import './BlockchainTimeline.css';

export const BlockchainTimeline = () => {
  const reversedBlocks = useMemo(() => [...BLOCKS].reverse(), []);
  const careerBlocks = useMemo(() => BLOCKS.filter(block => block.type !== 'origin'), []);
  const confirmedCareerBlocks = useMemo(
    () => careerBlocks.filter(block => block.confirmed),
    [careerBlocks]
  );
  const [selectedChain, setSelectedChain] = useState<ChainExperience | null>(null);
  const [showCvPreview, setShowCvPreview] = useState(false);
  const sectionRefs = useRef<HTMLElement[]>([]);
  const paginationRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const activeIndexRef = useRef(0);

  const totalSections = reversedBlocks.length + 2;

  const updatePaginationDots = useCallback((newIndex: number) => {
    if (!paginationRef.current || activeIndexRef.current === newIndex) return;
    const dots = paginationRef.current.children;
    const prev = dots[activeIndexRef.current] as HTMLElement | undefined;
    const next = dots[newIndex] as HTMLElement | undefined;
    if (prev) prev.classList.remove('dot-active');
    if (next) next.classList.add('dot-active');
    activeIndexRef.current = newIndex;
  }, []);

  // Pause hero chain logo animations when header scrolls offscreen
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.remove('header-offscreen');
        } else {
          el.classList.add('header-offscreen');
        }
      },
      { threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-section-index'));
            if (!isNaN(index)) updatePaginationDots(index);
          }
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    sectionRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [reversedBlocks.length, updatePaginationDots]);

  const scrollToSection = (index: number) => {
    const el = sectionRefs.current[index];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="blockchain-timeline">
      <nav
        className="block-pagination"
        aria-label="Block navigation"
        ref={paginationRef}
      >
        {Array.from({ length: totalSections }, (_, i) => {
          let dotClass = 'pagination-dot';
          let label = '';

          if (i === 0) {
            dotClass += ' dot-header dot-active';
            label = '0xDiscostu';
          } else if (i === totalSections - 1) {
            dotClass += ' dot-footer';
            label = 'EOF';
          } else {
            const block = reversedBlocks[i - 1];
            if (block) {
              dotClass += block.confirmed ? ' dot-confirmed' : ' dot-mining';
              label = block.company;
            }
          }

          return (
            <button
              key={i}
              className={dotClass}
              onClick={() => scrollToSection(i)}
              aria-label={label}
            >
              <span className="pagination-label">{label}</span>
            </button>
          );
        })}
      </nav>

      {/* Header */}
      <header
        className="timeline-header"
        data-section-index={0}
        ref={el => { if (el) { sectionRefs.current[0] = el; headerRef.current = el; } }}
      >
        {CHAIN_DECORATIONS.map((deco) => {
          const chain = getChainById(deco.chainId);
          if (!chain) return null;
          return (
            <button
              key={deco.chainId}
              className="chain-logo-decoration"
              onClick={() => setSelectedChain(chain)}
              aria-label={`Learn about ${chain.name}`}
              style={{
                top: deco.top,
                left: deco.left,
                right: deco.right,
                width: `${deco.size}px`,
                height: `${deco.size}px`,
                opacity: deco.opacity,
                '--deco-rotation': `${deco.rotation}deg`,
                '--deco-duration': `${deco.duration}s`,
              } as React.CSSProperties}
            >
              <img src={chain.icon} alt="" className="chain-logo-img" />
            </button>
          );
        })}
        <div className="header-content">
          <h1 className="glitch" data-text="0xDiscostu">0xDiscostu</h1>
          <p className="subtitle">Skills: Business Developer &bull; Marketing &bull; Engineer &bull; Product &bull; Web3</p>
          <p className="subtitle subtitle-supercharged">Agentic AI Supercharged</p>
          <div className="chain-stats">
            <div className="stat">
              <span className="stat-value">{careerBlocks.length}</span>
              <span className="stat-label">BLOCKS</span>
            </div>
            <div className="stat">
              <span className="stat-value">{confirmedCareerBlocks.length}</span>
              <span className="stat-label">CONFIRMED</span>
            </div>
            <div className="stat">
              <span className="stat-value">10+</span>
              <span className="stat-label">YEARS</span>
            </div>
          </div>
          <div className="header-links">
            <a href="https://github.com/swdiscordia" target="_blank" rel="noopener noreferrer">
              <FaGithub /> GitHub
            </a>
            <a href="https://x.com/Discostuu92" target="_blank" rel="noopener noreferrer">
              <FaXTwitter /> X
            </a>
            <a href="mailto:contact@0xdiscostu.com">
              <HiOutlineMail /> contact@0xdiscostu.com
            </a>
            <button onClick={() => setShowCvPreview(true)} className="header-cv-link">
              <HiOutlineDocumentDownload /> Download CV
            </button>
          </div>
        </div>
        <div className="scroll-indicator">
          <span>Scroll to mine</span>
          <div className="scroll-arrow">&darr;</div>
        </div>
      </header>

      {/* Blockchain */}
      <div className="chain-container">
        {reversedBlocks.map((block, index) => (
          <div
            key={block.id}
            className="block-section"
            data-section-index={index + 1}
            ref={el => { if (el) sectionRefs.current[index + 1] = el; }}
          >
            <div className="block-wrapper">
              <BlockCard
                block={block}
                displayIndex={index}
              />
            </div>
            {index < reversedBlocks.length - 1 && <ChainConnector />}
          </div>
        ))}
      </div>

      {/* Footer */}
      <footer
        className="timeline-footer"
        data-section-index={totalSections - 1}
        ref={el => { if (el) sectionRefs.current[totalSections - 1] = el; }}
      >
        <div className="footer-content">
          <p className="footer-hash">
            Chain validated &bull; {BLOCKS.length} blocks confirmed
          </p>
          <div className="footer-links">
            <a href="https://github.com/swdiscordia" target="_blank" rel="noopener noreferrer">
              <FaGithub /> GitHub
            </a>
            <a href="https://x.com/Discostuu92" target="_blank" rel="noopener noreferrer">
              <FaXTwitter /> X
            </a>
            <a href="mailto:contact@0xdiscostu.com">
              <HiOutlineMail /> contact@0xdiscostu.com
            </a>
            <button onClick={() => setShowCvPreview(true)} className="footer-cv-link">
              <HiOutlineDocumentDownload /> Download CV
            </button>
          </div>
        </div>
      </footer>

      <ChainModal chain={selectedChain} onClose={() => setSelectedChain(null)} />
      <CvPreviewModal open={showCvPreview} onClose={() => setShowCvPreview(false)} />
    </div>
  );
};
